export type PaidFeatures = Record<string, boolean>;
export type PaidFeatureExpiries = Record<string, string | Date | undefined>;

export const hasActivePaidFeature = (
  paidFeatures: PaidFeatures | undefined,
  expiries: PaidFeatureExpiries | undefined,
  featureKey: string,
  now = Date.now()
): boolean => {
  if (!paidFeatures?.[featureKey]) return false;

  const expiry = expiries?.[featureKey];
  if (!expiry) return true;

  const expiryTime = new Date(expiry).getTime();
  return Number.isFinite(expiryTime) && expiryTime > now;
};

export const getActivePaidFeatures = (
  paidFeatures: PaidFeatures | undefined,
  expiries: PaidFeatureExpiries | undefined,
  now = Date.now()
): PaidFeatures => Object.fromEntries(
  Object.entries(paidFeatures || {}).map(([featureKey, isPaid]) => [
    featureKey,
    Boolean(isPaid) && hasActivePaidFeature(paidFeatures, expiries, featureKey, now)
  ])
);

export const countActivePaidFeatures = (
  paidFeatures: PaidFeatures | undefined,
  expiries: PaidFeatureExpiries | undefined,
  now = Date.now()
): number => Object.values(getActivePaidFeatures(paidFeatures, expiries, now)).filter(Boolean).length;

export const hasGlobalPremiumAccess = (user: {
  isSubscribed?: boolean;
  isGoldMember?: boolean;
  isGold?: boolean;
  isSchoolSubscribed?: boolean;
} | null | undefined): boolean => Boolean(
  user?.isSubscribed || user?.isGoldMember || user?.isGold || user?.isSchoolSubscribed
);

export const getPaidFeatureStatus = (
  user: {
    isSubscribed?: boolean;
    isGoldMember?: boolean;
    isGold?: boolean;
    isSchoolSubscribed?: boolean;
    paidFeatures?: PaidFeatures;
    paidFeatureExpiries?: PaidFeatureExpiries;
  } | null | undefined,
  featureKey: string,
  now = Date.now()
) => {
  const globalAccess = hasGlobalPremiumAccess(user);
  const expiry = user?.paidFeatureExpiries?.[featureKey];
  const expiryTime = expiry ? new Date(expiry).getTime() : Number.NaN;
  const hasValidExpiry = Number.isFinite(expiryTime);
  const active = globalAccess || hasActivePaidFeature(user?.paidFeatures, user?.paidFeatureExpiries, featureKey, now);
  const expired = !globalAccess && hasValidExpiry && expiryTime <= now;

  return {
    active,
    expired,
    status: active ? 'Active' : expired ? 'Expired' : 'Inactive',
    expiryLabel: globalAccess
      ? 'Included with full-access membership'
      : hasValidExpiry
        ? `${expired ? 'Expired' : 'Expires'} ${new Date(expiryTime).toLocaleDateString()}`
        : active
          ? 'Expiry date not recorded'
          : 'No active access',
  };
};